import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useState, useEffect, useRef, useCallback } from 'react';
import FeedbackButton from './FeedbackButton';
import PageTransition from './PageTransition';
import OfflineBanner from './OfflineBanner';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { useTheme } from '../hooks/useTheme';
import { getPendingSales } from '../services/db';

const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const WARN_BEFORE_MS  = 60 * 1000;

const API = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

const NAV_ICONS = {
  '/pos': (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2"/>
      <path d="M8 21h8M12 17v4"/>
    </svg>
  ),
  '/dashboard': (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
      <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
    </svg>
  ),
  '/inventory': (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
    </svg>
  ),
  '/purchases': (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11l3 3L22 4"/>
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
    </svg>
  ),
  '/sales': (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
    </svg>
  ),
  '/customers': (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  '/finances': (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>
  ),
  '/analytics': (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
    </svg>
  ),
  '/debts': (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
    </svg>
  ),
  '/team': (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  '/pricing': (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  ),
  '/developer': (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
    </svg>
  ),
  '/inventory/intelligence': (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/>
    </svg>
  ),
  '/crm': (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
    </svg>
  ),
  '/financials': (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
      <line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/>
    </svg>
  ),
  '/reports': (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
      <polyline points="10 9 9 9 8 9"/>
    </svg>
  ),
  '/ai': (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a10 10 0 0 1 10 10c0 5.52-4.48 10-10 10S2 17.52 2 12 6.48 2 12 2z"/>
      <path d="M12 6v6l4 2"/>
      <circle cx="12" cy="12" r="2" fill="currentColor"/>
    </svg>
  ),
  '/staff': (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <line x1="19" y1="8" x2="19" y2="14"/>
      <line x1="22" y1="11" x2="16" y2="11"/>
    </svg>
  ),
  '/security': (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  ),
  '/settings': (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
  '/branches': (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  ),
  '/aging': (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
  '/close': (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
      <path d="M9 16l2 2 4-4"/>
    </svg>
  ),
  '/orders': (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="12" y1="17" x2="8" y2="17"/>
      <polyline points="10 9 9 9 8 9"/>
    </svg>
  ),
};

// Trial = Basic features. Plans listed are the minimum required to see the item.
const GROWTH_PLANS     = ['growth', 'enterprise', 'pro', 'business'];
const ENTERPRISE_PLANS = ['enterprise', 'business'];

const NAV_SECTIONS = [
  {
    label: 'MAIN',
    items: [
      { to: '/pos',        label: 'Quick Sale',  pos: true, permission: 'pos:use'  },
      { to: '/dashboard',  label: 'Dashboard'              },
      { to: '/sales',      label: 'Sales', permission: 'sales:view'   },
      { to: '/orders',     label: 'Sales Orders', permission: 'sales:view' },
    ],
  },
  {
    label: 'CUSTOMERS',
    items: [
      { to: '/customers',  label: 'Customers', permission: 'customers:view'                                      },
      { to: '/crm',        label: 'CRM', badge: 'crm', permission: 'crm:view', plans: GROWTH_PLANS              },
    ],
  },
  {
    label: 'INVENTORY',
    items: [
      { to: '/inventory',              label: 'Inventory',     permission: 'inventory:view'                                          },
      { to: '/purchases',              label: 'Purchases',     permission: 'purchases:view'                                          },
      { to: '/inventory/intelligence', label: 'Intelligence',  badge: 'invAlert', permission: 'inventory:activity:view', plans: ENTERPRISE_PLANS },
    ],
  },
  {
    label: 'FINANCE',
    items: [
      { to: '/finances',   label: 'Finances',         permission: 'finances:view'                          },
      { to: '/analytics',  label: 'Analytics',        permission: 'analytics:view',  plans: GROWTH_PLANS   },
      { to: '/debts',      label: 'Debt Tracker',     permission: 'debt:view'                              },
      { to: '/financials', label: 'Financial Intel',  permission: 'finances:view',   plans: GROWTH_PLANS   },
      { to: '/reports',    label: 'Reports',          permission: 'reports:view'                           },
      { to: '/aging',      label: 'Aging Reports',    permission: 'reports:view',    plans: GROWTH_PLANS   },
      { to: '/close',      label: 'Month-end Close',  ownerOnly: true,               plans: GROWTH_PLANS   },
    ],
  },
  {
    label: 'AI',
    items: [
      { to: '/ai', label: 'AI Insights', ai: true, badge: 'aiScore', permission: 'ai:view', plans: ENTERPRISE_PLANS },
    ],
  },
  {
    label: 'STAFF',
    items: [
      { to: '/staff', label: 'Staff & Payroll', permission: 'staff:view', plans: GROWTH_PLANS },
    ],
  },
  {
    label: 'OTHER',
    items: [
      { to: '/team',    label: 'Team'    },
      { to: '/pricing', label: 'Pricing' },
    ],
  },
  {
    label: 'SETTINGS',
    items: [
      { to: '/branches', label: 'Branches',  ownerOnly: true                              },
      { to: '/security', label: 'Security',  ownerOnly: true, plans: ENTERPRISE_PLANS     },
      { to: '/settings', label: 'Settings'                                                },
    ],
  },
];

const PLAN_BADGE = {
  trial:      { label: 'Trial',      bg: 'rgba(212,160,23,0.16)', color: '#F8D778' },
  free:       { label: 'Trial',      bg: 'rgba(212,160,23,0.16)', color: '#F8D778' },
  basic:      { label: 'Basic',      bg: 'rgba(212,160,23,0.18)', color: '#D4A017' },
  growth:     { label: 'Growth',     bg: 'rgba(212,160,23,0.20)', color: '#E8B923' },
  enterprise: { label: 'Enterprise', bg: 'rgba(212,160,23,0.25)', color: '#F0C040' },
  // legacy
  pro:        { label: 'Pro',        bg: 'rgba(212,160,23,0.18)', color: '#D4A017' },
  business:   { label: 'Business',   bg: 'rgba(212,160,23,0.22)', color: '#F0C040' },
};

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function getInitials(name) {
  return (name || 'U').split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase();
}

function formatDate() {
  return new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { can, role } = usePermissions();
  const { isDark, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [debtCount, setDebtCount]         = useState(0);
  const [invAlertCount, setInvAlertCount] = useState(0);
  const [crmAlertCount, setCrmAlertCount] = useState(0);
  const [aiScore, setAiScore]             = useState(null);
  const [pendingCount, setPendingCount]   = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const notifRef = useRef(null);
  const { isOnline } = useOnlineStatus();
  const [updateReady, setUpdateReady] = useState(false);

  // ── Electron auto-update listener ─────────────────────────────────────────
  useEffect(() => {
    if (!window.electronAPI?.isDesktop) return;
    window.electronAPI.onUpdateDownloaded(() => setUpdateReady(true));
  }, []);

  // ── Idle auto-logout ───────────────────────────────────────────────────
  const [idleWarning, setIdleWarning]     = useState(false);
  const [countdown, setCountdown]         = useState(60);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const idleTimerRef    = useRef(null);
  const countdownRef    = useRef(null);
  const idleWarningRef  = useRef(false); // ref mirror avoids stale-closure in resetIdleTimer

  const clearCountdown = useCallback(() => {
    clearInterval(countdownRef.current);
    countdownRef.current = null;
  }, []);

  const startCountdown = useCallback(() => {
    idleWarningRef.current = true;
    setIdleWarning(true);
    setCountdown(60);
    clearCountdown();
    countdownRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearCountdown();
          logout().then(() => navigate('/'));
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  }, [logout, navigate, clearCountdown]);

  const resetIdleTimer = useCallback(() => {
    clearTimeout(idleTimerRef.current);
    if (idleWarningRef.current) {
      idleWarningRef.current = false;
      setIdleWarning(false);
      clearCountdown();
    }
    idleTimerRef.current = setTimeout(startCountdown, IDLE_TIMEOUT_MS - WARN_BEFORE_MS);
  }, [startCountdown, clearCountdown]);

  useEffect(() => {
    if (!user) return;
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((e) => document.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach((e) => document.removeEventListener(e, resetIdleTimer));
      clearTimeout(idleTimerRef.current);
      clearCountdown();
    };
  }, [user, resetIdleTimer, clearCountdown]);

  useEffect(() => {
    const tok = localStorage.getItem('token');
    if (!tok) return;
    if (can('debt:view')) {
      fetch(`${API}/debts`, { headers: { Authorization: `Bearer ${tok}` } })
        .then((r) => r.ok ? r.json() : [])
        .then((data) => {
          if (Array.isArray(data)) setDebtCount(data.filter((d) => d.status !== 'paid').length);
        })
        .catch(() => {});
    } else {
      setDebtCount(0);
    }

    if (can('inventory:activity:view')) {
      Promise.all([
        fetch(`${API}/inventory-alerts/expiring?days=30`, { headers: { Authorization: `Bearer ${tok}` } })
          .then((r) => r.ok ? r.json() : {}),
        fetch(`${API}/inventory-alerts/reorder`, { headers: { Authorization: `Bearer ${tok}` } })
          .then((r) => r.ok ? r.json() : { totalCount: 0 }),
      ]).then(([exp, reorder]) => {
        const expCount = ((exp.expired || []).length + (exp.critical || []).length);
        setInvAlertCount(expCount + (reorder.totalCount || 0));
      }).catch(() => {});
    } else {
      setInvAlertCount(0);
    }

    if (can('crm:view')) {
      fetch(`${API}/crm/overview`, { headers: { Authorization: `Bearer ${tok}` } })
        .then((r) => r.ok ? r.json() : {})
        .then((data) => {
          setCrmAlertCount((data.atRiskCount || 0) + (data.birthdayThisWeek || 0));
        })
        .catch(() => {});
    } else {
      setCrmAlertCount(0);
    }

    if (can('ai:view')) {
      fetch(`${API}/ai/health-score`, { headers: { Authorization: `Bearer ${tok}` } })
        .then((r) => r.ok ? r.json() : null)
        .then((data) => { if (data?.overallScore != null) setAiScore(data.overallScore); })
        .catch(() => {});
    } else {
      setAiScore(null);
    }
  }, [pathname, role]);

  useEffect(() => {
    const check = async () => {
      const pending = await getPendingSales();
      setPendingCount(pending.length);
    };
    check();
    const interval = setInterval(check, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!showNotifications) return;
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showNotifications]);

  const notifications = [
    pendingCount > 0 && {
      id: 'pending',
      icon: '📤',
      title: `${pendingCount} sale${pendingCount !== 1 ? 's' : ''} queued offline`,
      sub: 'Will sync when back online',
      color: '#F97316',
      to: '/pos',
    },
    debtCount > 0 && {
      id: 'debt',
      icon: '💳',
      title: `${debtCount} unpaid balance${debtCount !== 1 ? 's' : ''}`,
      sub: 'Customers with outstanding credit',
      color: '#EF4444',
      to: '/debts',
    },
    invAlertCount > 0 && {
      id: 'inventory',
      icon: '📦',
      title: `${invAlertCount} inventory alert${invAlertCount !== 1 ? 's' : ''}`,
      sub: 'Low stock or expiring items',
      color: '#F59E0B',
      to: '/inventory/intelligence',
    },
    crmAlertCount > 0 && {
      id: 'crm',
      icon: '👥',
      title: `${crmAlertCount} CRM alert${crmAlertCount !== 1 ? 's' : ''}`,
      sub: 'At-risk customers or birthdays',
      color: '#8B5CF6',
      to: '/crm',
    },
    aiScore != null && aiScore < 60 && {
      id: 'ai',
      icon: '🤖',
      title: `Health score is ${aiScore}/100`,
      sub: 'Review AI recommendations',
      color: '#06B6D4',
      to: '/ai',
    },
  ].filter(Boolean);

  const totalNotifCount = notifications.length;

  const handleLogout = () => { logout(); navigate('/'); };
  const firstName = user?.name?.split(' ')[0] || 'there';
  const badge = PLAN_BADGE[user?.plan || 'trial'];

  return (
    <div className="app-layout">
      <OfflineBanner />
      {updateReady && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
          background: '#1E3A5F', color: '#F1F5F9',
          padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16,
          borderBottom: '2px solid #D4A017', fontSize: '0.85rem', fontWeight: 600,
        }}>
          <span>A new version of OptiKa is ready.</span>
          <button
            onClick={() => window.location.reload()}
            style={{ background: '#D4A017', color: '#000', border: 'none', borderRadius: 6, padding: '5px 16px', fontWeight: 700, cursor: 'pointer', fontSize: '0.82rem' }}
          >
            Restart &amp; Update
          </button>
        </div>
      )}
      {/* ── Sidebar ───────────────────────────────────────────────────────── */}
      <aside className="sidebar">

        {/* Logo */}
        <div className="sidebar-logo">
          <img
            src="/optika-logo-white.svg"
            alt="OptiKa"
            style={{ height: '32px', width: 'auto', display: 'block', marginBottom: 6 }}
          />
          <div style={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
            {user?.businessName || 'Business Manager'}
            {!isOnline && (
              <span style={{ color: '#F87171', fontSize: '0.6rem', fontWeight: 700 }}>● Offline</span>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {NAV_SECTIONS.map(({ label, items }) => {
            const visible = items.filter(({ plans, ownerOnly, permission }) => {
              if (plans && !plans.includes(user?.plan)) return false;
              if (ownerOnly && role !== 'owner') return false;
              if (permission && !can(permission)) return false;
              return true;
            });
            if (!visible.length) return null;
            return (
              <div key={label}>
                <div className="nav-section-label">{label}</div>
                {visible.map(({ to, label: itemLabel, pos, badge, ai }) => (
                  <NavLink
                    key={to}
                    to={to}
                    className={({ isActive }) => `nav-item${isActive ? ' active' : ''}${pos ? ' pos-nav-item' : ''}`}
                    style={ai ? {
                      background: 'linear-gradient(135deg, rgba(91,45,142,0.12), rgba(212,160,23,0.1))',
                      border: '1px solid rgba(212,160,23,0.22)',
                      color: '#D4A017',
                      fontWeight: 700,
                      borderRadius: 9,
                      margin: '2px 0',
                    } : undefined}
                  >
                    <span style={{ flexShrink: 0, opacity: 0.9 }}>{NAV_ICONS[to]}</span>
                    <span style={{ flex: 1 }}>{itemLabel}</span>
                    {to === '/pos' && pendingCount > 0 && (
                      <span style={{
                        background: '#F97316', color: 'white', borderRadius: 10,
                        fontSize: '0.6rem', fontWeight: 800, padding: '1px 6px', lineHeight: 1.6, flexShrink: 0,
                      }}>{pendingCount}</span>
                    )}
                    {to === '/debts' && debtCount > 0 && (
                      <span style={{
                        background: '#DC2626', color: 'white', borderRadius: 10,
                        fontSize: '0.6rem', fontWeight: 800, padding: '1px 6px', lineHeight: 1.6, flexShrink: 0,
                      }}>{debtCount}</span>
                    )}
                    {badge === 'invAlert' && invAlertCount > 0 && (
                      <span style={{
                        width: 8, height: 8, borderRadius: '50%', background: '#DC2626',
                        flexShrink: 0, boxShadow: '0 0 0 2px rgba(220,38,38,0.3)',
                      }} />
                    )}
                    {badge === 'crm' && crmAlertCount > 0 && (
                      <span style={{
                        width: 8, height: 8, borderRadius: '50%', background: '#D97706',
                        flexShrink: 0, boxShadow: '0 0 0 2px rgba(217,119,6,0.3)',
                      }} />
                    )}
                    {badge === 'aiScore' && aiScore != null && (
                      <span style={{
                        background: 'rgba(212,160,23,0.25)', color: '#D4A017',
                        borderRadius: 8, fontSize: '0.6rem', fontWeight: 800,
                        padding: '1px 6px', lineHeight: 1.6, flexShrink: 0,
                      }}>{aiScore}</span>
                    )}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        {/* Help button */}
        <div style={{ padding: '8px 12px 0' }}>
          <button
            onClick={() => setShowHelpModal(true)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 10,
              background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 9, padding: '9px 12px', cursor: 'pointer', color: 'rgba(255,255,255,0.55)',
              fontSize: '0.8rem', fontWeight: 500, transition: 'all 150ms',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'white'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = 'rgba(255,255,255,0.55)'; }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            Help & Getting Started
          </button>
        </div>

        {/* User card */}
        <div style={{ padding: '12px 12px 16px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{
            background: 'rgba(255,255,255,0.06)',
            borderRadius: 10,
            padding: '10px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
            <div style={{
              width: 34, height: 34, borderRadius: '50%',
              background: 'linear-gradient(135deg, #D4A017, #F0C040)',
              color: '#0F172A', fontWeight: 800, fontSize: '0.75rem',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'Sora, sans-serif', flexShrink: 0,
            }}>
              {getInitials(user?.name)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user?.name || 'User'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                <span style={{
                  fontSize: '0.6rem', fontWeight: 700, padding: '1px 7px',
                  borderRadius: 20, background: badge.bg, color: badge.color,
                  letterSpacing: '0.04em',
                }}>
                  {badge.label.toUpperCase()}
                </span>
              </div>
            </div>
            <button
              onClick={handleLogout}
              title="Sign out"
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'rgba(255,255,255,0.35)', padding: 4, borderRadius: 6,
                display: 'flex', alignItems: 'center',
                transition: 'color 150ms',
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.75)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.35)'}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </button>
          </div>
        </div>
      </aside>

      {/* ── Main ──────────────────────────────────────────────────────────── */}
      <main className="main-content">

        {/* Top header */}
        <div className="top-header">
          <div>
            <div style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>
              {getGreeting()},&nbsp;
              <span style={{ fontWeight: 700, color: 'var(--navy, #1B3A6B)', fontFamily: 'Sora, sans-serif' }}>
                {firstName}
              </span>
              &nbsp;👋
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--muted)', marginTop: 1 }}>
              {formatDate()}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* Dark mode toggle */}
            <button
              onClick={toggleTheme}
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              style={{
                background: isDark ? 'rgba(212,160,23,0.12)' : 'none',
                border: 'none',
                cursor: 'pointer',
                color: isDark ? '#D4A017' : 'var(--muted)',
                padding: 7,
                borderRadius: 9,
                display: 'flex',
                alignItems: 'center',
                transition: 'background 150ms, color 150ms',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? 'rgba(212,160,23,0.2)' : '#F1F5F9'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = isDark ? 'rgba(212,160,23,0.12)' : 'none'; }}
            >
              {isDark ? (
                /* Sun icon */
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="5"/>
                  <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                  <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                </svg>
              ) : (
                /* Moon icon */
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                </svg>
              )}
            </button>
            {/* Bell */}
            <div ref={notifRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setShowNotifications((v) => !v)}
                title="Notifications"
                style={{
                  background: showNotifications ? (isDark ? 'rgba(212,160,23,0.12)' : '#F1F5F9') : 'none',
                  border: 'none', cursor: 'pointer',
                  color: 'var(--muted)', padding: 7, borderRadius: 9,
                  display: 'flex', alignItems: 'center', position: 'relative',
                  transition: 'background 150ms',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.08)' : '#F1F5F9'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = showNotifications ? (isDark ? 'rgba(212,160,23,0.12)' : '#F1F5F9') : 'none'; }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
                {totalNotifCount > 0 && (
                  <span style={{
                    position: 'absolute', top: 5, right: 5,
                    minWidth: 7, height: 7, borderRadius: '50%',
                    background: '#EF4444', border: '1.5px solid white',
                  }} />
                )}
              </button>

              {showNotifications && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                  width: 320, borderRadius: 14,
                  background: isDark ? '#1E293B' : 'white',
                  border: `1px solid ${isDark ? '#334155' : '#E2E8F0'}`,
                  boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                  zIndex: 100,
                  overflow: 'hidden',
                }}>
                  <div style={{
                    padding: '12px 16px',
                    borderBottom: `1px solid ${isDark ? '#334155' : '#F1F5F9'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <span style={{ fontWeight: 700, fontSize: '0.84rem', color: isDark ? '#F1F5F9' : '#0F172A', fontFamily: 'Sora, sans-serif' }}>
                      Notifications
                    </span>
                    {totalNotifCount > 0 && (
                      <span style={{
                        background: '#EF4444', color: 'white',
                        fontSize: '0.62rem', fontWeight: 800,
                        padding: '1px 7px', borderRadius: 20,
                      }}>
                        {totalNotifCount}
                      </span>
                    )}
                  </div>

                  {totalNotifCount === 0 ? (
                    <div style={{ padding: '28px 16px', textAlign: 'center' }}>
                      <div style={{ fontSize: '1.8rem', marginBottom: 8 }}>🔔</div>
                      <div style={{ fontSize: '0.8rem', fontWeight: 600, color: isDark ? '#94A3B8' : '#64748B' }}>
                        All caught up!
                      </div>
                      <div style={{ fontSize: '0.72rem', color: isDark ? '#475569' : '#94A3B8', marginTop: 3 }}>
                        No alerts right now
                      </div>
                    </div>
                  ) : (
                    <div>
                      {notifications.map((n) => (
                        <button
                          key={n.id}
                          onClick={() => { navigate(n.to); setShowNotifications(false); }}
                          style={{
                            width: '100%', textAlign: 'left',
                            padding: '11px 16px',
                            border: 'none', borderBottom: `1px solid ${isDark ? '#1E293B' : '#F8FAFC'}`,
                            background: 'none', cursor: 'pointer',
                            display: 'flex', alignItems: 'flex-start', gap: 12,
                            transition: 'background 120ms',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : '#F8FAFC'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                        >
                          <span style={{
                            width: 34, height: 34, borderRadius: 10, flexShrink: 0,
                            background: `${n.color}18`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '1rem',
                          }}>
                            {n.icon}
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: isDark ? '#F1F5F9' : '#0F172A', marginBottom: 2 }}>
                              {n.title}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: isDark ? '#64748B' : '#94A3B8' }}>
                              {n.sub}
                            </div>
                          </div>
                          <span style={{ fontSize: '0.7rem', color: n.color, fontWeight: 700, flexShrink: 0, marginTop: 2 }}>→</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* Avatar */}
            <div className="user-avatar" title={user?.name}>
              {getInitials(user?.name)}
            </div>
          </div>
        </div>

        {/* Page content */}
        <PageTransition key={pathname}>
          {children}
        </PageTransition>
      </main>

      <FeedbackButton />

      {/* ── Idle timeout warning ──────────────────────────────────────── */}
      {idleWarning && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#1E2235', border: '1px solid #2A2E45', borderRadius: 18,
            padding: '36px 40px', maxWidth: 380, width: '90%', textAlign: 'center',
            boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: 'rgba(240,180,41,0.12)', border: '1px solid rgba(240,180,41,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#F0B429" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#E8EDF5', marginBottom: 8, fontFamily: 'Sora, sans-serif' }}>
              Still there?
            </div>
            <div style={{ fontSize: '0.84rem', color: '#8892A4', lineHeight: 1.6, marginBottom: 24 }}>
              You've been inactive for a while. You'll be signed out in
            </div>
            <div style={{
              fontSize: '2.8rem', fontWeight: 800, color: countdown <= 10 ? '#FF6B6B' : '#F0B429',
              fontFamily: 'JetBrains Mono, monospace', lineHeight: 1, marginBottom: 24,
              transition: 'color 300ms',
            }}>
              {countdown}s
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                onClick={() => { logout().then(() => navigate('/')); }}
                style={{
                  background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.25)',
                  color: '#FF6B6B', borderRadius: 10, padding: '10px 20px',
                  fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                Log out
              </button>
              <button
                onClick={resetIdleTimer}
                style={{
                  background: '#F0B429', border: 'none', color: '#0B0E17',
                  borderRadius: 10, padding: '10px 22px',
                  fontSize: '0.85rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                Stay logged in
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Help Modal ─────────────────────────────────────────────────────── */}
      {showHelpModal && (
        <div
          onClick={() => setShowHelpModal(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#1E293B', borderRadius: 16, padding: 28,
              width: '100%', maxWidth: 560,
              boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#F1F5F9' }}>Getting Started</h2>
              <button
                onClick={() => setShowHelpModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', padding: 4, display: 'flex', borderRadius: 6 }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <p style={{ color: '#94A3B8', fontSize: '0.85rem', marginBottom: 16, marginTop: 0 }}>
              Watch this short walkthrough to get up and running quickly.
            </p>

            {/* TODO: Replace with <iframe src="YOUR_VIDEO_URL" ... allowFullScreen /> */}
            <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', borderRadius: 10, overflow: 'hidden', background: '#0F172A', border: '1px solid #334155' }}>
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#2563EB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>
                </div>
                <p style={{ color: '#64748B', fontSize: '0.85rem', margin: 0 }}>Walkthrough video coming soon</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
